import { CATEGORY_SEPARATOR } from '../constants';

import {
  type CategoryFilterDepartments,
  type CategoryFilterGroups,
  type CategoryFiltersResponse,
  type GetGroupsByIdResponse,
} from '@/modules/categories/aliases';
import {
  CATEGORY_PATH_TO_GENDER,
  everythingElseId,
  GENDER_TO_DEPARTMENT_ID,
  kidswearId,
} from '@/modules/categories/constants';
import { filterByDepartment } from '@/modules/categories/departmentFiltering';

export function decodeCompositeValue(compositeValue: string) {
  const [department, group] = compositeValue.split('.');

  return { department, group };
}

export function getCompositeValue(department?: string, group?: string) {
  if (!department || !group) {
    return;
  }

  return `${department}.${group}`;
}

export function getCategoryOptionsV2(data?: CategoryFiltersResponse) {
  const { departments = [], groups = {} } = data ?? {};

  return departments.flatMap((department) =>
    department.children.flatMap((group) => {
      const options = filterByDepartment(groups[group.id], department.id).map(
        (productType) => ({
          label: productType.name,
          value: getCompositeValue(department.id, productType.id),
        })
      );

      if (!options.length) {
        return [];
      }
      return [
        {
          label: `${department.name} ${CATEGORY_SEPARATOR} ${group.name}`,
          value: department.id,
          options,
        },
      ];
    })
  );
}

export function getDepartmentOption({
  departments,
  departmentId,
}: {
  departments?: CategoryFilterDepartments;
  departmentId: string;
}) {
  const found = departments?.find(({ id }) => {
    return id === departmentId;
  });

  return found ? { value: found.id, label: found.name } : undefined;
}

export function getProductTypeOption({
  groups,
  productType,
  group,
}: {
  groups?: CategoryFilterGroups;
  productType: string;
  group: string;
}) {
  const productTypes = (groups ?? {})[group];
  const found = productTypes?.find(({ id }) => id === productType);

  return found ? { value: found.id, label: found.name } : undefined;
}

export function getDepartmentId(gender?: string, isKids?: boolean) {
  if (isKids) {
    return kidswearId;
  }

  if (!gender) {
    return everythingElseId;
  }

  return GENDER_TO_DEPARTMENT_ID[gender];
}

/**
 * Accepts both male/female (search) and mens/womens (shop) gender values.
 * Returns undefined when no department is selected, so nothing is filtered.
 */
export function getSearchDepartmentId(gender?: string, isKids?: boolean) {
  if (isKids) {
    return kidswearId;
  }

  if (!gender) {
    return undefined;
  }

  return GENDER_TO_DEPARTMENT_ID[CATEGORY_PATH_TO_GENDER[gender] ?? gender];
}

export function getGenderId(department: string) {
  return Object.keys(GENDER_TO_DEPARTMENT_ID).find(
    (key) => GENDER_TO_DEPARTMENT_ID[key] === department
  );
}

export function getSubcategoryOptions({
  group,
  groups,
  department,
}: {
  group?: string;
  groups?: CategoryFilterGroups;
  department?: string;
}) {
  if (!group || !groups) {
    return [];
  }

  const subcategories = filterByDepartment(groups[group], department)
    .filter(({ name }) => name)
    .map(({ id, name }) => ({
      label: name,
      value: id,
    }));

  return subcategories;
}

export function isKidswear(department: string) {
  return department === kidswearId;
}

export function getGroupFromProductType(
  groupsById: GetGroupsByIdResponse,
  productType: string
): string | undefined {
  const matchingGroup = Object.values(groupsById).find((group) =>
    group.product_types.some(
      (productTypeItem) => productTypeItem.id === productType
    )
  );

  return matchingGroup?.id;
}

export function createGetGroupFromProductType(
  groupsById?: GetGroupsByIdResponse
) {
  return function (productType?: string) {
    if (!groupsById || !productType) {
      return undefined;
    }
    const matchingGroup = Object.values(groupsById).find((group) =>
      group.product_types.some(
        (productTypeItem) => productTypeItem.id === productType
      )
    );

    return matchingGroup?.id;
  };
}
